import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  X,
  Sparkles,
  ShoppingCart,
  ArrowRight,
  Gift,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface NewFeatureBannerProps {
  isVisible: boolean;
  onDismiss: () => void;
  onExplore: () => void;
}

const NewFeatureBanner: React.FC<NewFeatureBannerProps> = ({
  isVisible,
  onDismiss,
  onExplore,
}) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 text-white shadow-lg"
        >
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="p-2 bg-white/20 rounded-full"
                >
                  <Sparkles className="h-5 w-5" />
                </motion.div>
                
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                    <Gift className="h-3 w-3 mr-1" />
                    {t("announcement.new")}
                  </Badge>
                  <span className="font-semibold">
                    {t("announcement.stockAndPOS")}
                  </span>
                  <span className="hidden sm:inline text-white/90">
                    - {t("announcement.bannerDescription")}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onExplore}
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30 hover:border-white/50"
                >
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t("announcement.tryNow")}</span>
                  <span className="sm:hidden">{t("announcement.try")}</span>
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                  className="h-8 w-8 p-0 text-white hover:bg-white/20"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Animated Background Pattern */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-32 h-32 bg-white/5 rounded-full"
                animate={{
                  x: [0, 100, 0],
                  y: [0, -20, 0],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 8 + i * 2,
                  repeat: Infinity,
                  delay: i * 2,
                }}
                style={{
                  left: `${20 + i * 30}%`,
                  top: "-50%",
                }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewFeatureBanner;
